import { useParams } from "react-router-dom";
import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetFeaturePredictionsQuery } from "../featuresSlice";
import { selectTrainPost } from "../trainPostsSlice";
import { apiSlice } from "../../api/apiSlice";
import useSearchParams from "../../../hooks/useSearchParams";
import usePaging from "../../../hooks/usePaging";
import paramConfig from "../../../config/paramConfig";

const DEFAULT_BUCKET = "true_positive";
const DEFAULT_SORT = "probability";
// Re-fire the predictions GET while inference is outstanding. The GET doubles
// as the trigger that drives inference forward, so polling both advances the
// backend work and picks up results as they settle.
const POLL_INTERVAL_MS = 5000;

const useFeaturePredictionsBlock = () => {
  const dispatch = useDispatch();
  const { getParam } = useSearchParams();
  const { activePage } = usePaging({});
  const { featureId } = useParams();
  const bucket = getParam(paramConfig.BUCKET) || DEFAULT_BUCKET;
  const sort = getParam(paramConfig.SORT) || DEFAULT_SORT;
  const link = useCallback((rowData) => `/spectra/${rowData}`, []);

  // Driven by the effect below: 0 when settled, POLL_INTERVAL_MS while
  // `missing`/`pending` work is still outstanding.
  const [pollingInterval, setPollingInterval] = useState(0);
  const wasBusyRef = useRef(false);

  const { data, isLoading, isFetching, refetch } =
    useGetFeaturePredictionsQuery(
      { id: featureId, bucket, sort, page: activePage },
      { skip: featureId == null, pollingInterval }
    );

  // The Model tab names the active model it sees, and invalidates these
  // predictions when a run makes one live, but RTK drops that refetch while a
  // predictions GET is in flight. So once nothing is in flight, predictions of
  // another model are fetched again — once per model and view, since a view
  // cached before the model went live is served again without a request.
  const liveModelId = useSelector(
    (state) => selectTrainPost(state, featureId).liveModelId
  );
  const checkedViewRef = useRef(null);
  const checkKey = `${liveModelId}|${bucket}|${sort}|${activePage}`;
  const hasData = data != null;
  const predictionsModelId = data?.model?.id ?? null;
  useEffect(() => {
    if (liveModelId == null || isFetching || !hasData) return;
    if (checkedViewRef.current === checkKey) return;
    checkedViewRef.current = checkKey;
    if (predictionsModelId !== liveModelId) refetch();
  }, [liveModelId, checkKey, isFetching, hasData, predictionsModelId, refetch]);

  // `missing` = spectra with no inference yet (the next GET triggers them);
  // `pending` = inference in flight. Poll until both clear.
  const missing = data?.counts?.missing ?? 0;
  const pending = data?.counts?.pending ?? 0;

  useEffect(() => {
    const busy = missing > 0 || pending > 0;
    setPollingInterval(busy ? POLL_INTERVAL_MS : 0);
    // Inference just settled: the active model's live_evaluation
    // (pending/errored + live counts) is now stale — refresh the model cache.
    if (wasBusyRef.current && !busy && featureId != null) {
      dispatch(
        apiSlice.util.invalidateTags([{ type: "FeatureModel", id: featureId }])
      );
    }
    wasBusyRef.current = busy;
  }, [missing, pending, featureId, dispatch]);

  const rows = useMemo(() => {
    if (!Array.isArray(data?.items)) return [];
    return data.items.map((item) => ({
      spectrum: item?.spectrum,
      value: item?.current_label,
      probability: item?.probability,
      inference_state: item?.inference_state,
      last_edit_at: item?.last_edit_at,
    }));
  }, [data?.items]);

  const items = useMemo(
    () => [
      {
        id: "predictionsArrayHoverable",
        type: "spectraArrayHoverable",
        data: rows,
        featureId,
        link,
        showProbability: true,
      },
      { id: "notes", formOnly: true },
    ],
    [rows, featureId, link]
  );

  return {
    items,
    hasItems: rows.length > 0,
    counts: data?.counts || {},
    model: data?.model || null,
    pagination: data?.pagination || null,
    isLoading,
    isFetching,
    bucket,
    sort,
  };
};

export default useFeaturePredictionsBlock;
